import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { Nav, Platform, MenuController, Events, ToastController, ModalController } from 'ionic-angular';
import { Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs/Rx";
import { IUser } from "../models/user.model";
import { IEvent, IClientConfig, IClientModules } from "../models/client.model";
import { EventData } from '../providers/event';
import * as fromClient from '../ngrx/reducers/client.reducer';
import * as fromAuth from '../ngrx/reducers/auth.reducer';
import * as authActions from '../ngrx/actions/auth.actions';
import { ENV } from '@app/env';
import { IEmbed } from '../models/emebd.model';
import { EmbedData } from '../providers/embed';
import { ClientData } from '../providers/client';
import { UtilitiesProvider } from '../providers/utilities';
import { TranslateService } from '@ngx-translate/core';
import { has } from 'lodash';
import { TranslateCodePipe } from '../pipes/translate';
import { GamificationProvider } from '../providers/gamification';
import moment from 'moment';
import { AuthData } from '../providers/auth';
import uniqid from 'uniqid';
import { AnalyticsData, _ACTIONS } from '../providers/analytics';
import { App } from 'ionic-angular/components/app/app';

@Component({
  templateUrl: 'app.html'
})
export class RebusApp implements OnInit, OnDestroy {
  @ViewChild(Nav) nav: Nav;

  version: string;
  client$: Observable<string>;
  clientConfig$: Observable<IClientConfig>;
  clientModules$: Observable<any>;
  user$: Observable<IUser>;
  userNotification$: Observable<number>;
  event$: Observable<IEvent>;
  eventModules$: Observable<any>;
  referred: any;
  embedPage$: Observable<IEmbed[]>;
  rootPage: any;
  eventId: string;

  navigatePrivateMessage: boolean = false;

  private userInformationSubs: Subscription;
  private userInformation: IUser;

  menuItems: { module: string, title: string, icon: string, cmp?: string, order?: number }[];
  menuEventItems: { module: string, title: any, icon: string, cmp?: string }[];

  constructor(
    app: App,
    public platform: Platform,
    public menuCtrl: MenuController,
    private store: Store<any>,
    private eventData: EventData,
    private clientData: ClientData,
    private embedData: EmbedData,
    private event: Events,
    private utilities: UtilitiesProvider,
    private translate: TranslateService,
    private toastCtrl: ToastController,
    private translatePipe: TranslateCodePipe,
    private gamificationProvider: GamificationProvider,
    private analyticsProvider: AnalyticsData,
    private authData: AuthData,
    private modalCtrl: ModalController
  ) {
    this.platform.ready().then(() => {
      this.platform.registerBackButtonAction(() => {
        app.navPop();
      });
    });
  }

  ngOnInit() {
    this.userInformationSubs = this.getUserInformation();
    this.translate.get('Notifications').subscribe(result => this.utilities.consoleDecorator('Notifications', result));
    this.version = ENV.version;
    this.client$ = this.store.select(fromClient.getClientId);
    this.clientConfig$ = this.store.select(fromClient.getClientConfig);
    this.clientModules$ = this.store.select(fromClient.getClientModules);
    this.user$ = this.store.select(fromAuth.getUser);
    this.initMenu();
    this.referred = this.subReferred();
    this.generateFirstAnalytics();
  }

  /**
   * Mecanismo que se encarga de generar la información de analytics para marcaje en big data
   */
  private generateFirstAnalytics() {
    // ID unico de carga de usuario
    const trackId = uniqid();
    window.localStorage.setItem(`${ENV.client}-PROCESS-LOAD`, trackId);
    return this.authData.saveFirstTrackOnFirestore();
  }

  /**
   * Obnservable que verifica si hay información de un referido
   * TODO: Verificar necesidad de este metodo
   */
  private subReferred() {
    return this.client$.subscribe(clientId => {
      if (clientId) {
        this.clientData.getReferred(clientId)
          .subscribe(result => {
            return result;
          });
      }
    });
  }

  /**
   * Inicializa el menu general de la app
   */
  private initMenu() {
    this.menuCtrl.enable(false, 'generalMenu');
    this.menuCtrl.enable(false, 'eventMenu');

    this.menuItems = this.initMenuGeneral();

    this.event.subscribe('menuEvent', (eventId: string = '') => {
      if (this.utilities.validateEventIdParam(eventId)) {
        this.eventId = eventId;
        this.client$.subscribe(clientId => {
          if (clientId) {
            this.event$ = this.eventData.getEventInfo(clientId, eventId);
            this.eventModules$ = this.eventData.getEventModules(clientId, eventId);
            this.embedPage$ = this.embedData.getEmbedsByEvent(clientId, eventId)
              .map(list => list.sort((a: any, b: any) => a.order - b.order));
            this.menuEventItems = this.initMenuEvent;
          }
        });
      }
    });

    this.client$.subscribe(clientId => {
      if (clientId) {
        this.clientData.getReferred(clientId).subscribe(result => {
          this.referred = result;
        });
      }
    });

    // ID unico de carga de usuario
    const trackId = uniqid();
    window.localStorage.setItem(`${ENV.client}-PROCESS-LOAD`, trackId);
    this.authData.saveFirstTrackOnFirestore();
    this.getIfPrivateVersionBrowser();

    return;
  }

  private getIfPrivateVersionBrowser() {
    let storageTestKey = 'sTest',
      storage = window.sessionStorage;

    try {
      storage.setItem(storageTestKey, 'test');
      storage.removeItem(storageTestKey);
    } catch (e) {
      if (e.code === DOMException.QUOTA_EXCEEDED_ERR && storage.length === 0) {
        this.navigatePrivateMessage = true;
      } else {
        throw e;
      }
    }
  }

  public getUrlPhotoUser(photos) {
    if (photos) {
      return `url('${photos}')`;
    }
  }

  /**
   * Carga la informacion del usuario
   */
  private getUserInformation() {
    return this.store.select(fromAuth.getUser)
      .subscribe((userInformation) => {
        this.userInformation = userInformation;

        if (has(userInformation, 'info')) {
          if (userInformation.info.lang) {
            this.translate.setDefaultLang(userInformation.info.lang);
          } else {
            this.translate.setDefaultLang(this.translate.getBrowserLang());
          }
          this.userNotification$ = this.authData.getUserDB(userInformation.info.id, ENV.client)
            .filter(user => user !== null && user !== undefined)
            .mergeMap(user => {
              const userTime = user.lastViewNotification;
              return this.gamificationProvider.getPointsWithoutReview(user.id, ENV.client)
                .map(points => {
                  if (!user.lastViewNotification) return null;
                  return points.filter((p: any) => {
                    const pTime = moment(p.created_at).format();
                    const uTime = moment(userTime).format();
                    return pTime > uTime
                  }).length
                });
            })
        } else {
          this.translate.setDefaultLang(this.translate.getBrowserLang());
        }
      })
  }

  /**
   * Show the banner to open the dialog to accept the notifications.
   * Reason: If the user denied the notification, he will need to enter to the browser option to allow again.
   *
   */
  private initMenuGeneral() {
    // this.clientModules$.subscribe(configMenu => {
    //   if (configMenu) {
    return [
      {
        module: 'notification',
        title: 'Notifications',
        icon: 'md-notifications',
        cmp: 'RequestsPage',
        // order: configMenu['notification'].order || 1
      },
      {
        module: 'services',
        title: 'Services',
        icon: 'list',
        cmp: 'service-flow'
      },
      {
        module: 'my_reserves',
        title: 'My purchases',
        icon: 'md-bookmarks',
        cmp: 'MyPurchasesPage',
        // order: configMenu['my_reserves'].order || 2
      },
      {
        module: 'event',
        title: 'Events and activities',
        icon: 'md-calendar',
        cmp: 'room',
        // order: configMenu['event'].order || 3
      },
      {
        module: 'favorites',
        title: 'My favorites',
        icon: 'md-star',
        cmp: 'favorites',
        // order: configMenu['favorites'].order || 4
      },
      {
        module: 'staff',
        title: 'Team',
        icon: 'md-people',
        cmp: 'staff-list',
        // order: configMenu['staff'].order || 5
      },
      {
        module: 'news',
        title: 'News',
        icon: 'globe',
        cmp: 'News',
        // order: configMenu['news'].order || 6
      },
      {
        module: 'notes',
        title: 'Notes',
        icon: 'create',
        cmp: 'notes',
        // order: configMenu['notes'].order || 7
      },
      {
        module: 'interactivity',
        title: 'Interactivity',
        icon: 'md-thumbs-up',
        cmp: 'activity',
        // order: configMenu['favorites'].order || 8
      },
      {
        module: 'catalog',
        title: 'Catalog',
        icon: 'md-pricetags',
        cmp: 'categoriesProduct',
        // order: configMenu['catalog'].order || 9
      },
      {
        module: 'networking',
        title: 'Networking',
        icon: 'md-contacts',
        cmp: 'networking-contact',
        // order: configMenu['networking'].order || 10
      },
      {
        module: 'sponsor',
        title: 'Sponsors',
        icon: 'megaphone',
        cmp: 'sponsor-list',
        // order: configMenu['sponsor'].order || 11
      },
      {
        module: 'gamification',
        title: 'Gamification',
        icon: 'md-medal',
        cmp: 'PointsPage',
        // order: configMenu['gamification'].order || 12
      },
      {
        module: 'resource',
        title: 'Resources',
        icon: 'md-photos',
        cmp: 'SourcesPage',
        // order: configMenu['resource'].order || 13
      },
      {
        module: 'commercial_samples',
        title: 'Commercial samples',
        icon: 'md-easel',
        cmp: 'CommercialSamplesListPage',
        // order: configMenu['commercial_samples'].order || 14
      },
      {
        module: 'likes',
        title: 'Interest',
        icon: 'md-flag',
        cmp: 'interest',
        // order: configMenu['likes'].order || 15
      },
      {
        module: 'pay_methods',
        title: 'Payment methods',
        icon: 'md-card',
        cmp: 'PaymentMethodsPage',
        // order: configMenu['pay_methods'].order || 16
      },
      {
        module: 'coupons',
        title: 'Coupons',
        icon: 'md-barcode',
        cmp: 'coupon-book',
        // order: configMenu['coupons'].order || 17
      },
      {
        module: 'referred',
        title: 'Referred',
        icon: 'md-people',
        cmp: 'referrals',
        // order: configMenu['resource'].order || 18
      },
      {
        module: 'ticket',
        title: 'Tickets',
        icon: 'md-pricetags',
        cmp: 'TicketsPage',
        // order: configMenu['tickets'].order || 18
      },
      {
        module: 'guest',
        title: 'Guest',
        icon: 'md-people',
        cmp: 'MyGuestClubPage',
        // order: configMenu['terms'].order || 21
      },
      {
        module: 'beneficiary',
        title: 'Beneficiary',
        icon: 'contacts',
        cmp: 'ClubBeneficiariesPage',
        // order: configMenu['terms'].order || 22
      },
      {
        module: 'pqr',
        title: 'PQR',
        icon: 'document',
        cmp: 'PqrPage',
        // order: configMenu['terms'].order || 22
      },
      {
        module: 'directory',
        title: 'Directory',
        icon: 'list-box',
        cmp: 'DirectoryListPage',
        // order: configMenu['terms'].order || 23
      },
      {
        module: 'sostenimiento',
        title: 'Sostenimiento',
        icon: 'cash',
        cmp: 'MaintenancePaymentsClubPage',
        // order: configMenu['terms'].order || 24
      },
      {
        module: 'terms',
        title: 'Terms',
        icon: 'md-paper',
        cmp: 'terms',
        // order: configMenu['terms'].order || 19
      },
      {
        module: 'legal_terms_service',
        title: 'Conditions Service',
        icon: 'md-paper',
        cmp: 'legal_terms_service',
        // order: configMenu['terms'].order || 20
      },
    ];
    //   }
    // })
  }

  private get initMenuEvent() {
    return [
      {
        module: 'home',
        title: 'Home',
        icon: 'home',
        cmp: 'room'
        // order: configMenu['room'].order || 1
      },
      // {
      //   module: 'notification',
      //   title: 'Notifications',
      //   icon: 'md-notifications',
      //   cmp: 'RequestsPage',
      //   // order: configMenu['notification'].order || 2
      // },
      {
        module: 'my_reserves',
        title: 'My purchases',
        icon: 'md-bookmarks',
        cmp: 'MyPurchasesPage',
        // order: configMenu['my_reserves'].order || 3
      },
      {
        module: 'services',
        title: 'Services',
        icon: 'list',
        cmp: 'service-flow'
      },
      {
        module: 'favorites',
        title: 'My favorites',
        icon: 'md-star',
        cmp: 'favorites',
        // order: configMenu['favorites'].order || 4
      },
      {
        module: 'diary',
        title: 'Agenda',
        icon: 'calendar',
        cmp: 'Agenda',
        // order: configMenu['diary'].order || 5
      },
      {
        module: 'staff',
        title: 'Team',
        icon: 'people',
        cmp: 'staff-list',
        // order: configMenu['staff'].order || 6
      },
      {
        module: 'map',
        title: 'Map',
        icon: 'create',
        cmp: 'EventMapPage',
        // order: configMenu['map'].order || 7
      },
      {
        module: 'networking',
        title: 'Networking',
        icon: 'md-contacts',
        cmp: 'networking-contact',
        // order: configMenu['networking'].order || 8
      },
      {
        module: 'interactivity',
        title: 'Interactivity',
        icon: 'md-thumbs-up',
        cmp: 'activity',
        // order: configMenu['interactivity'].order || 9
      },
      {
        module: 'catalog',
        title: 'Catalog',
        icon: 'md-pricetags',
        cmp: 'categoriesProduct',
        // order: configMenu['catalog'].order || 10
      },
      {
        module: 'news',
        title: 'News',
        icon: 'globe',
        cmp: 'News',
        // order: configMenu['news'].order || 11
      },
      {
        module: 'timeline',
        title: 'Timeline',
        icon: 'md-text',
        cmp: 'TimelinePage',
        // order: configMenu['timeline'].order || 12
      },
      {
        module: 'social',
        title: 'Social',
        icon: 'people',
        cmp: 'Social',
        // order: configMenu['social'].order || 13
      },
      {
        module: 'sponsor',
        title: 'Sponsors',
        icon: 'megaphone',
        cmp: 'sponsor-list',
        // order: configMenu['sponsor'].order || 14
      },
      {
        module: 'resource',
        title: 'Resources',
        icon: 'md-photos',
        cmp: 'SourcesPage',
        // order: configMenu['resource'].order || 15
      },
      {
        module: 'notes',
        title: 'Notes',
        icon: 'create',
        cmp: 'notes',
        // order: configMenu['notes'].order || 16
      },
      {
        module: 'streaming',
        title: 'Streaming',
        icon: 'md-videocam',
        cmp: 'event-streaming',
        // order: configMenu['streaming'].order || 17
      },
      {
        module: 'commercial_samples',
        title: 'Commercial samples',
        icon: 'md-easel',
        cmp: 'CommercialSamplesListPage',
        // order: configMenu['commercial_samples'].order || 18
      }
    ];
  }

  isShowEmbed(embed, item, position) {
    return embed.component === item.module && embed.position === position;
  }

  goBeneficiaryPage() {
    return this.nav.setRoot('ClubBeneficiariesPage', { animate: true, direction: 'forward' });
  }

  goGuestPage() {
    return this.nav.setRoot('MyGuestClubPage', { animate: true, direction: 'forward' });
  }

  goPQRPage() {
    return this.nav.setRoot('PqrPage', { animate: true, direction: 'forward' });
  }

  goDirectory() {
    return this.nav.setRoot('DirectoryListPage', { animate: true, direction: 'forward' });

  }

  goPaymentsPartner() {
    return this.nav.setRoot('MaintenancePaymentsClubPage', { animate: true, direction: 'forward' });
  }

  isShowMenuEvent(item, eventModules, user: IUser) {
    switch (item.module) {
      case 'event':
      case 'profile':
      case 'logout':
        // case 'favorites': // Esto debe ser desde el admin
        // case 'notification':
        // case 'pay_methods':
        return true;
      default:
        return eventModules[item.module];
    }
  }

  isShowMenuGeneral(item, clientModules: IClientModules, user: IUser) {
    switch (item.module) {
      case 'profile':
      case 'logout':
        // case 'likes':
        // case 'terms':
        // case 'favorites':
        // case 'notification':
        return true;

      case 'referred':
        return this.validateReferred(item, clientModules, user);

      default:
        return clientModules[item.module];
    }
  }

  public isSocio(user, module) {
    switch (module) {
      case "beneficiary":
        if (user.info.isTitular) {
          return true;
        } else {
          return false
        }
      case "sostenimiento":
        if (user.info.isTitular) {
          return true;
        } else {
          return false
        }
      default:
        return true;
    }
  }

  /**
   * Valida si el usuario cuenta con el perfil necesario para mostrar el modulo de referidos
   */
  private validateReferred(item, clientModules, user) {
    if (!user.info.profile) return false;
    if (!clientModules[item.module]) return false;

    return Object.keys(this.referred.profiles).map(profile => {
      return profile === user.info.profile ? true : false;
    });
  }

  async itemSelectedGeneralMenu(cmp, user: IUser = null) {
    switch (cmp) {
      // TODO: Ojo que hay que montar la redireccion automatica de catalogo segun la configuracion del cliente
      case 'UserEditPage':
      case 'PaymentMethodsPage':
        return this.nav.push(cmp, { eventId: '--' });

      case 'logout':
        this.utilities.setLoading();
        return this.analyticsProvider
          .savePageTrackOnFirestorePromise(
            "AuthPage",
            _ACTIONS.logout,
            window.localStorage.getItem(`${ENV.client}-PROCESS-LOAD`),
            user,
          ).then(() => this.store.dispatch(new authActions.Logout()));

      case 'networking-contact':
        if (user) {
          if (user.info.isNetworking) {
            return this.nav.setRoot(cmp, { eventId: '--' }, { animate: true, direction: 'forward' });
          }
        }
        this.toastCtrl.create({
          message: await this.translatePipe.transform('You must complete your profile and activate the networking before continuing'),
          duration: 3000,
          position: 'top'
        }).present();
        return this.nav.push('UserEditPage', { eventId: '--' });

      case 'terms':
        return this.nav.push('TermsPage');

      case 'legal_terms_service':
        return this.nav.push('ConditionsPage');
      default:
        return this.nav.setRoot(cmp, { eventId: '--' }, { animate: true, direction: 'forward' });
    }

  }

  async itemSelectedEventMenu(cmp, user: IUser = null) {
    this.utilities.consoleDecorator('Route event', cmp);

    switch (cmp) {
      case 'UserEditPage':
      case 'PaymentMethodsPage':
        return this.nav.push(cmp, { eventId: this.eventId });

      case 'room':
        return this.nav.setRoot(cmp, {}, { animate: true, direction: 'forward' });

      case 'logout':
        this.utilities.setLoading();
        return this.analyticsProvider
          .savePageTrackOnFirestorePromise(
            "AuthPage",
            _ACTIONS.logout,
            window.localStorage.getItem(`${ENV.client}-PROCESS-LOAD`),
            user,
          ).then(() => this.store.dispatch(new authActions.Logout()));


      case 'networking-contact':
        if (user) {
          if (user.info.isNetworking) {
            return this.nav.setRoot(cmp, { eventId: this.eventId }, { animate: true, direction: 'forward' });
          }
        }
        this.toastCtrl.create({
          message: await this.translatePipe.transform('You must complete your profile and activate the networking before continuing'),
          duration: 3000,
          position: 'top'
        }).present();
        return this.nav.push('UserEditPage', { eventId: this.eventId });

      default:
        return this.nav.setRoot(cmp, { eventId: this.eventId }, { animate: true, direction: 'forward' });
    }

  }

  showModalScranQR() {
    let modalQRScanner = this.modalCtrl.create('QrReaderTicketPage', { enableBackdropDismiss: false });
    modalQRScanner.present();
    modalQRScanner.onDidDismiss(result => {
      if (result) {
        const dataQR = result;
        let modalResultData = this.modalCtrl.create('QRResultDataTicketPage', { enableBackdropDismiss: false, data: dataQR });
        modalResultData.present();
      }
    })
  }

  showModalScranQRReserve() {
    let modalQRScanner = this.modalCtrl.create('QrReaderUserPage', { enableBackdropDismiss: false });
    modalQRScanner.present();
    modalQRScanner.onDidDismiss(result => {
      if (result) {
        const dataQR = result;
        let modalResultData = this.modalCtrl.create('QrResultDataUserPage', { enableBackdropDismiss: false, data: dataQR });
        modalResultData.present();
      }
    })
  }

  showModalScranQRUser() {
    let modalQRScanner = this.modalCtrl.create('QrReaderValidateUserPage', { enableBackdropDismiss: false });
    modalQRScanner.present();
    modalQRScanner.onDidDismiss(result => {
      if (result) {
        const dataQR = result;
        let modalResultData = this.modalCtrl.create('QrResultDataValidateUserPage', { enableBackdropDismiss: false, data: dataQR });
        modalResultData.present();
      }
    })
  }

  embedSelect(embedId) {
    this.nav.setRoot('EmbeddedPage', { eventId: this.eventId, embedId }, { animate: true, direction: 'forward' });
  }

  ngOnDestroy() {
    if (this.userInformationSubs) this.userInformationSubs.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts